import { Button, Text } from '@cretesuite/design-system'
import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonItem,
  IonLabel,
  IonList,
  IonSelect,
  IonSelectOption,
  IonTextarea,
  IonTitle,
  IonToolbar,
} from '@ionic/react'
import { msg } from '@lingui/core/macro'
import { useLingui } from '@lingui/react'
import { useEffect, useMemo, useState } from 'react'
import { useMutation } from 'urql'
import type { GeolocationInput } from '../../gql/graphql'
import { ChangeTaskMutation } from '../../graphql/timeclock'
import { submitQueueable } from '../../helpers/submitQueueable'
import { useTimeClock } from '../../hooks/timeclock/useTimeClock'
import { useTimeClockTasks } from '../../hooks/timeclock/useTimeClockTasks'
import { useNetwork } from '../../hooks/useNetwork'
import { useToast } from '../../hooks/useToast'
import { useGeoLocation } from './hooks/useGeolocation'

type ChangeTaskModalProps = {
  onDismiss: () => void
}

export const ChangeTaskModal = ({ onDismiss }: ChangeTaskModalProps) => {
  const { _ } = useLingui()
  const { isOnline } = useNetwork()
  const [presentToast] = useToast()
  const { data: currentTimeClock } = useTimeClock()
  const { tasks } = useTimeClockTasks()
  const [, changeTask] = useMutation(ChangeTaskMutation)
  const { getCurrentCoords, preFetchGeolocation } = useGeoLocation({
    actionText: 'change task',
  })

  const currentTask = currentTimeClock?.task

  const selectableTasks = useMemo(
    () => tasks.filter((t) => t.id !== currentTask?.id),
    [tasks, currentTask],
  )

  const [selectedTask, setSelectedTask] = useState<number | undefined>()
  const [note, setNote] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const selected = selectableTasks.find((t) => t.id === selectedTask)
  const noteRequired = selected?.isNoteRequired ?? false
  const isGeoRequired = selected?.isGeoRequired ?? false

  useEffect(() => {
    if (!isGeoRequired) return
    preFetchGeolocation()
  }, [isGeoRequired, preFetchGeolocation])

  const handleSubmit = async () => {
    if (!selectedTask) {
      setError(_(msg`Please select a task`))
      return
    }
    if (noteRequired && !note.trim()) {
      setError(_(msg`A comment is required for this task`))
      return
    }

    setError(null)
    setSubmitting(true)

    try {
      let geolocation: GeolocationInput | undefined
      if (isGeoRequired) {
        const coords = await getCurrentCoords()
        if (!coords) return
        geolocation = coords
      }

      const exec = changeTask({
        data: {
          note: note || undefined,
          task: { id: selectedTask },
          geolocation,
        },
      })

      // Never block on the network — the optimistic update already reflects the
      // task change. Offline or a too-slow connection → queue and sync in the
      // background; a responsive connection still surfaces real server errors.
      const outcome = await submitQueueable(exec, { isOnline })
      if (outcome.status === 'error') {
        setError(outcome.result.error?.message ?? _(msg`An error occurred`))
        return
      }

      presentToast({
        color: 'success',
        message:
          outcome.status === 'queued'
            ? _(msg`Saved. It will sync when you're back online.`)
            : _(msg`Task changed.`),
        duration: 3000,
      })
      onDismiss()
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : _(msg`An error occurred`))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <>
      <IonHeader className='ion-no-border'>
        <IonToolbar>
          <IonTitle>{_(msg`Change Task`)}</IonTitle>
          <IonButtons slot='end'>
            <IonButton onClick={onDismiss}>{_(msg`Close`)}</IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent fullscreen>
        <IonList>
          {selectableTasks.length > 0 && (
            <IonItem>
              <IonLabel position='stacked'>{_(msg`Task`)}</IonLabel>
              <IonSelect
                value={selectedTask}
                placeholder={
                  currentTask
                    ? `${_(msg`Current`)}: ${currentTask.name}`
                    : _(msg`Select task`)
                }
                interface='action-sheet'
                interfaceOptions={{
                  header: _(msg`Select a task`),
                }}
                onIonChange={(e) => setSelectedTask(e.detail.value)}
              >
                {selectableTasks.map((task) => (
                  <IonSelectOption key={task.id} value={task.id}>
                    {task.name}
                  </IonSelectOption>
                ))}
              </IonSelect>
            </IonItem>
          )}
          <IonItem>
            <IonLabel position='stacked'>
              {_(msg`Comments`)}
              {noteRequired ? ' (required)' : ''}
            </IonLabel>
            <IonTextarea
              value={note}
              onIonInput={(e) => setNote(e.detail.value ?? '')}
              placeholder={_(msg`Description of work being performed`)}
              autoGrow
            />
          </IonItem>
        </IonList>

        {error && (
          <div className='ion-padding'>
            <Text variant='label' className='text-destructive'>
              {error}
            </Text>
          </div>
        )}

        <div className='ion-text-center ion-padding'>
          <Button
            onClick={handleSubmit}
            disabled={submitting}
            variant='secondary'
          >
            {submitting ? _(msg`Submitting...`) : _(msg`Change Task`)}
          </Button>
        </div>
      </IonContent>
    </>
  )
}
